import { useAppTheme } from "../../../hooks/useAppTheme";
import type { ThemeColors } from "../../../utils/constants/colors";
import React, { useState, useEffect, useContext } from "react";
import {
  Platform,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Image,
} from "react-native";
import { Text, View } from "../../../components/shared/Themed";
import CustomTextInput from "../../../components/custom/CustomTextInput";
import { useNavigation } from "@react-navigation/native";
import { FontAwesome } from "@expo/vector-icons";
import FeatherIcon from "react-native-vector-icons/Feather";
import * as ImagePicker from "expo-image-picker";
import useColorScheme from "../../../hooks/useColorScheme";
import { updateUser } from "../../../services/usersService";
import { User } from "../../../models/usersModel";
import { googlePlaceAutoComplete } from "../../../services/generalService";
import { getS3SignUrl, pushPhotoToS3 } from "../../../services/s3BucketService";
import { useSelector } from "react-redux";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { ConfigurationDispatcher } from "../../../redux/dispatcher/configurationDispatcher";
import { actionTypesConfigurations } from "../../../redux/constants/actionTypesConfigurations";
import Spinner from "../../../components/shared/Spinner";
import * as Okta from "../../../services/authService";
import { getItemFromSessionStorage } from "../../../services/utils/storageService";

type ThemeOption = "light" | "system" | "dark";
const THEME_OPTIONS: { value: ThemeOption; label: string; icon: string }[] = [
  { value: "light", label: "Light", icon: "sun-o" },
  { value: "system", label: "System", icon: "adjust" },
  { value: "dark", label: "Dark", icon: "moon-o" },
];

export default function ProfileForm() {
  const { userInfo, profilePhoto, themeMode } = useSelector(
    (state: any) => state.configurations,
  );
  const { colors, isDark } = useAppTheme();
  const styles = createStyles(colors, isDark);
  const currentThemeMode: ThemeOption = (themeMode as ThemeOption) ?? "system";

  const setTheme = (value: ThemeOption) => {
    ConfigurationDispatcher(actionTypesConfigurations.ThemeMode, value);
  };
  const {
    first_name,
    last_name,
    email: userEmail,
    mobile: userMobile,
    gender: userGender,
    languages,
    location,
    user_id,
    login: userLogin,
  } = userInfo;

  const colorScheme = useColorScheme();
  const orientationContext = useContext(DeviceOrientationContext);

  const [givenName, setGivenName] = useState<string>(first_name);
  const [familyName, setFamilyName] = useState<string>(last_name);
  const [image, setImage] = useState<string>(profilePhoto);
  const [email, setEmail] = useState<string>(userEmail);
  const [mobile, setMobile] = useState<string>(userMobile);
  const [gender] = useState<string>(userGender);
  const [imageBase64, setImageBase64] = useState<string>("");
  const [isLoading, setLoading] = useState<boolean>(false);
  const [selectedLanguages, setSelectedLanguages] =
    useState<Array<string>>(languages);
  const [selectedLocation, setSelectedLocation] =
    useState<Array<string>>(location);
  const [locationSearchTerm, setLocationSearchTerm] = useState<string>("");
  const [, setLocationList] = useState<Array<unknown>>([]);
  const [, setLocation] = useState<string>("");
  const disabled = false;

  const [username, setUsername] = useState<string>(userLogin || userEmail);
  const [isResetLoading, setResetLoading] = useState<boolean>(false);
  const [, setStatus] = useState<string>("");

  const companyContext = getItemFromSessionStorage("company-context")
    ? JSON.parse(getItemFromSessionStorage("company-context") as string)
    : null;

  const disabledLogin =
    (companyContext && companyContext.company_name === "Multisteps") ||
    companyContext === null;

  const setHasAvatar = (data: boolean) => {
    ConfigurationDispatcher(actionTypesConfigurations.HasAvatar, data);
  };

  const setProfilePhoto = (data: string) => {
    ConfigurationDispatcher(actionTypesConfigurations.ProfilePhoto, data);
  };

  const pickImage = async () => {
    // No permissions request is necessary for launching the image library
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.3,
      base64: true,
      exif: false,
    });
    if (!result.canceled) {
      const res = result.assets[0];
      setImageBase64(res.base64 as string);
      setImage(res.uri);
    }
  };

  const getImage = async () => {
    setLoading(true);
    getS3SignUrl(
      "multisteps-go-public-image",
      `avatar/${user_id}.png`,
      "getObject",
      "image/*",
    ).then((data) => {
      if (data?.signedUrl) {
        setImage(data.signedUrl);
        setProfilePhoto(data.signedUrl);
      }
      setLoading(false);
    });
  };

  const uploadImage = async () => {
    setLoading(true);
    image &&
      getS3SignUrl(
        "multisteps-go-public-image",
        `avatar/${user_id}.png`,
        "putObject",
        "image/*",
      ).then((data) => {
        pushPhotoToS3(data?.signedUrl, "PUT", imageBase64, "image/*").then(
          () => {
            updateUser(
              {
                has_avatar: true,
              },
              "",
            ).then(() => {
              setLoading(false);
              setHasAvatar(true);
              getImage();
            });
          },
        );
      });
  };

  const handleTextChange = (text: string, field: string) => {
    field === "givenName"
      ? setGivenName(text)
      : field === "familyName"
        ? setFamilyName(text)
        : field === "mobile"
          ? setMobile(text)
          : field === "email"
            ? setEmail(text)
            : setLocation(text);
  };

  const handleLocationTextChange = (text: string) => {
    const isNewSearch = locationSearchTerm.length !== text.length;
    isNewSearch && setLocationSearchTerm(text);
    text &&
      isNewSearch &&
      googlePlaceAutoComplete(text as string).then((data) =>
        setLocationList([{ name: "Location", place_id: 0, children: data }]),
      );
  };

  const handleSubmit = () => {
    setLoading(true);

    const userBody: User = {
      email: email as string,
      first_name: givenName as string,
      last_name: familyName as string,
      gender: gender as string,
      location: selectedLocation as string[],
      mobile: mobile as string,
      languages: selectedLanguages as string[],
      display_name: `${givenName} ${familyName}`,
    };

    updateUser(userBody, user_id).then((data) => {
      if (data?.status === "ACTIVE") {
        imageBase64 && uploadImage();
        setLoading(false);
      }
    });
  };

  const submitPasswordReset = () => {
    setResetLoading(true);
    Okta.forgotPassword(email).then((data) => {
      setStatus(data.status);
      setResetLoading(false);
      // Removed navigation since we're already on the profile page
      alert("Password reset email sent.");
    });
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (Platform.OS !== "web" && isMounted) {
        const { status } =
          await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== "granted") {
          alert(
            "Sorry, Camera roll permissions are required to make this work!",
          );
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const navigation = useNavigation();
  const isMobile = orientationContext.isSmallDevice;

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[
        styles.scrollContent,
        isMobile && { paddingHorizontal: 14 },
      ]}
      showsVerticalScrollIndicator={false}
    >
      {/* Page Header */}
      <View
        style={[
          styles.pageHeader,
          isMobile && { paddingHorizontal: 14, marginHorizontal: -14 },
        ]}
      >
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {isMobile && (
            <TouchableOpacity
              onPress={() => navigation.navigate("SettingsM")}
              style={styles.backButton}
            >
              <FeatherIcon name="arrow-left" size={18} color="#2563EB" />
            </TouchableOpacity>
          )}
          <View style={{ flex: 1 }}>
            <Text style={[styles.pageTitle, isMobile && { fontSize: 20 }]}>
              Account Settings
            </Text>
            <Text style={[styles.pageSubtitle, isMobile && { fontSize: 12 }]}>
              Manage your profile information and security settings
            </Text>
          </View>
        </View>
      </View>

      {/* ---- Profile Card ---- */}
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Profile Information</Text>
        <View style={styles.sectionDivider} />

        {/* Avatar Row */}
        <View style={styles.avatarRow}>
          <View
            style={[
              styles.avatarRing,
              isMobile && { width: 64, height: 64, borderRadius: 32 },
            ]}
          >
            <Image
              source={{
                uri: image ? image : require("../../../assets/noimage.png"),
              }}
              style={[
                styles.photo,
                isMobile && { width: 58, height: 58, borderRadius: 29 },
              ]}
              resizeMode="cover"
            />
          </View>
          <View style={styles.avatarMeta}>
            <TouchableOpacity
              style={styles.uploadButton}
              onPress={() => pickImage()}
            >
              <Text style={styles.uploadButtonText}>Upload Photo</Text>
            </TouchableOpacity>
            <Text style={styles.avatarHint}>
              JPG, GIF or PNG. Max size of 8MB
            </Text>
          </View>
        </View>

        {/* Name Fields */}
        <View
          style={{
            flexDirection: orientationContext.isSmallDevice ? "column" : "row",
            width: "100%",
            marginTop: 24,
          }}
        >
          <CustomTextInput
            title="First Name"
            width={orientationContext.isSmallDevice ? "100%" : "48%"}
            borderColor="#e5e7eb"
            marginLeft={0}
            placeHolder="First name"
            value={givenName}
            disabled={true}
            setChangeText={(text: string) =>
              handleTextChange(text, "givenName")
            }
          />
          <CustomTextInput
            title="Last Name"
            width={orientationContext.isSmallDevice ? "100%" : "48%"}
            borderColor="#e5e7eb"
            marginLeft={orientationContext.isSmallDevice ? 0 : 16}
            placeHolder="Last name"
            value={familyName}
            disabled={true}
            setChangeText={(text: string) =>
              handleTextChange(text, "familyName")
            }
          />
        </View>

        {/* Contact Fields */}
        <View
          style={{
            flexDirection: orientationContext.isSmallDevice ? "column" : "row",
            width: "100%",
            marginTop: orientationContext.isSmallDevice ? 8 : 16,
          }}
        >
          <CustomTextInput
            title="Username"
            width={orientationContext.isSmallDevice ? "100%" : "48%"}
            borderColor="#e5e7eb"
            marginLeft={0}
            marginTop={0}
            placeHolder="Username"
            value={username}
            disabled={true}
            setChangeText={() => {}}
          />
          <CustomTextInput
            title="Email"
            width={orientationContext.isSmallDevice ? "100%" : "48%"}
            borderColor="#e5e7eb"
            marginLeft={orientationContext.isSmallDevice ? 0 : 16}
            placeHolder="Email address"
            value={email}
            disabled={true}
            setChangeText={(text: string) => handleTextChange(text, "email")}
          />
        </View>

        {/* Buttons */}
        <View
          style={[styles.buttonRow, isMobile && { flexDirection: "column" }]}
        >
          {!disabled && (
            <TouchableOpacity
              style={[styles.primaryButton, isMobile && { width: "100%" }]}
              onPress={() => handleSubmit()}
            >
              {isLoading ? (
                <Spinner styles={styles.spinner} size="small" color="#fff" />
              ) : (
                <Text style={styles.primaryButtonText}>Save Changes</Text>
              )}
            </TouchableOpacity>
          )}
          {!disabledLogin && (
            <TouchableOpacity
              style={[
                styles.outlineButton,
                isMobile
                  ? { width: "100%", marginTop: 10, marginLeft: 0 }
                  : { marginLeft: 12 },
              ]}
              onPress={submitPasswordReset}
              disabled={disabledLogin || isResetLoading}
            >
              {isResetLoading ? (
                <Spinner size="small" color="#4f46e5" />
              ) : (
                <Text style={styles.outlineButtonText}>Reset Password</Text>
              )}
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* ---- Appearance Card ---- */}
      <View style={[styles.card, { marginTop: 0 }]}>
        <Text style={styles.sectionTitle}>Appearance</Text>
        <View style={styles.sectionDivider} />
        <Text style={[styles.sectionHint, { marginBottom: 12 }]}>
          Choose your preferred colour scheme.
        </Text>
        <View
          style={{
            flexDirection: "row",
            backgroundColor: colors.surfaceSubtle,
            borderRadius: 10,
            padding: 3,
          }}
        >
          {THEME_OPTIONS.map((opt) => (
            <TouchableOpacity
              key={opt.value}
              onPress={() => setTheme(opt.value)}
              activeOpacity={0.7}
              style={{
                flex: 1,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                paddingVertical: 9,
                borderRadius: 8,
                gap: 6,
                backgroundColor:
                  currentThemeMode === opt.value
                    ? colors.surface
                    : "transparent",
                shadowColor:
                  currentThemeMode === opt.value ? "#000" : "transparent",
                shadowOffset: { width: 0, height: 1 },
                shadowOpacity: currentThemeMode === opt.value ? 0.1 : 0,
                shadowRadius: 2,
                elevation: currentThemeMode === opt.value ? 2 : 0,
              }}
            >
              <FontAwesome
                name={opt.icon as any}
                size={14}
                color={
                  currentThemeMode === opt.value
                    ? colors.text
                    : colors.textMuted
                }
              />
              <Text
                style={{
                  fontSize: 13,
                  fontWeight: currentThemeMode === opt.value ? "600" : "500",
                  color:
                    currentThemeMode === opt.value
                      ? colors.text
                      : colors.textMuted,
                }}
              >
                {opt.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </ScrollView>
  );
}

function createStyles(colors: ThemeColors, isDark: boolean) {
  return StyleSheet.create({
    scrollView: {
      flex: 1,
      backgroundColor: isDark ? "#1F2937" : "#F9FAFB",
    },
    scrollContent: {
      paddingHorizontal: 24,
      paddingBottom: 48,
    },
    pageHeader: {
      paddingTop: 20,
      paddingBottom: 16,
      marginBottom: 16,
      paddingHorizontal: 24,
      marginHorizontal: -24,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.borderSubtle,
    },
    backButton: {
      width: 38,
      height: 38,
      borderRadius: 12,
      backgroundColor: colors.surface,
      justifyContent: "center",
      alignItems: "center",
      marginRight: 12,
      borderWidth: 1,
      borderColor: colors.borderSubtle,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.06,
      shadowRadius: 3,
      elevation: 1,
    },
    pageTitle: {
      fontSize: 26,
      fontWeight: "800",
      color: colors.text,
      letterSpacing: -0.5,
    },
    pageSubtitle: {
      fontSize: 14,
      color: colors.textSecondary,
      marginTop: 4,
      letterSpacing: 0.1,
    },

    /* Card */
    card: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      paddingVertical: 20,
      paddingHorizontal: 20,
      marginBottom: 16,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 4,
      elevation: 1,
      overflow: "visible",
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: "700",
      color: colors.text,
      letterSpacing: -0.2,
    },
    sectionDivider: {
      height: 1,
      backgroundColor: colors.borderSubtle,
      marginVertical: 16,
    },
    sectionHint: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
      marginBottom: 8,
    },

    /* Avatar */
    avatarRow: {
      flexDirection: "row",
      alignItems: "center",
    },
    avatarRing: {
      width: 88,
      height: 88,
      borderRadius: 44,
      borderWidth: 3,
      borderColor: isDark ? "#2a3b4c" : "#e0e7ff",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      backgroundColor: colors.surfaceSubtle,
    },
    photo: {
      width: 82,
      height: 82,
      borderRadius: 41,
    },
    avatarMeta: {
      marginLeft: 20,
      justifyContent: "center",
    },
    uploadButton: {
      paddingVertical: 8,
      paddingHorizontal: 18,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.primary,
      backgroundColor: isDark ? colors.surfaceSubtle : "#F0F8FF",
    },
    uploadButtonText: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.primary,
    },
    avatarHint: {
      fontSize: 12,
      color: colors.textMuted,
      marginTop: 8,
    },

    /* Buttons */
    buttonRow: {
      flexDirection: "row",
      marginTop: 28,
    },
    primaryButton: {
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: 12,
      paddingHorizontal: 28,
      borderRadius: 10,
      backgroundColor: colors.primary,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 3,
    },
    primaryButtonText: {
      fontSize: 14,
      fontWeight: "700",
      color: "#ffffff",
      letterSpacing: 0.3,
    },
    outlineButton: {
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: 12,
      paddingHorizontal: 24,
      borderRadius: 10,
      borderWidth: 1.5,
      borderColor: colors.primary,
      backgroundColor: isDark ? "transparent" : "#F0F8FF",
    },
    outlineButtonText: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.primary,
      letterSpacing: 0.2,
    },

    /* Legacy (kept for compatibility) */
    container: {
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "100%",
    },
    box: {
      flex: 1,
      flexDirection: "column" as const,
    },
    subBox: {
      flexDirection: "row" as const,
      margin: 30,
    },
    subBox2: {
      flexDirection: "column" as const,
      marginLeft: "2%",
    },
    button: {
      alignItems: "center" as const,
      justifyContent: "center" as const,
      width: 100,
      height: 40,
      borderRadius: 10,
      elevation: 3,
      backgroundColor: "#6495ED",
      marginLeft: 14,
    },
    buttonText: {
      fontSize: 12,
      lineHeight: 21,
      fontWeight: "bold" as const,
      letterSpacing: 0.25,
      color: "white",
    },
    label: {
      minWidth: "10%",
      maxWidth: 500,
      fontSize: 15,
      fontWeight: "bold" as const,
      textAlign: "left" as const,
      marginLeft: 14,
    },
    greyLabel: {
      fontSize: 14,
      textAlign: "left" as const,
      lineHeight: 10,
      letterSpacing: 0.25,
      color: "#808080",
      marginLeft: 10,
    },
    input: {
      minWidth: "10%",
      maxWidth: 500,
      height: 60,
      backgroundColor: "#fff",
      paddingVertical: 10,
      paddingHorizontal: 15,
      borderColor: "#ccc",
      borderWidth: 2,
      borderRadius: 15,
      fontSize: 15,
      margin: 20,
    },
    photoButton: {
      alignItems: "center" as const,
      justifyContent: "center" as const,
      width: 130,
      height: 40,
      elevation: 2,
      margin: 15,
    },
    photoButtonText: {
      fontSize: 14,
      lineHeight: 10,
      letterSpacing: 0.25,
      color: "#87CEFA",
    },
    spinner: {
      marginLeft: 12,
    },
  });
}
